/**
 * @file api/client.js
 * @description Configured Axios instance for all backend API calls.
 *
 * A request interceptor automatically attaches the Azure Entra ID Bearer token
 * to every outgoing request. The token is acquired silently (from cache) using
 * MSAL; if silent acquisition fails (e.g. token expired) the user is redirected
 * to the Entra ID login page.
 *
 * Usage:
 *   import apiClient from '../api/client';
 *   const data = await apiClient.get('/api/tokens');
 */

import axios from 'axios';
import msalInstance from '../auth/msalInstance';
import { tokenRequest } from '../auth/msalConfig';

/** Base Axios instance pointed at the backend API. */
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001',
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Request interceptor — acquires an Entra ID token silently and injects it
 * into the Authorization header before the request is sent.
 */
apiClient.interceptors.request.use(
  async (config) => {
    const account = msalInstance.getActiveAccount();

    if (account) {
      try {
        // Try to get a token from cache without prompting the user
        const tokenResponse = await msalInstance.acquireTokenSilent({
          ...tokenRequest,
          account,
        });
        config.headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
      } catch (silentError) {
        // Silent acquisition failed (e.g. refresh token expired) — force re-login
        console.warn('[API] Silent token acquisition failed, redirecting to login.', silentError);
        await msalInstance.acquireTokenRedirect(tokenRequest);
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response interceptor — surfaces API error messages from the response body
 * as proper JavaScript Error objects so callers don't need to inspect the raw
 * Axios error themselves.
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const message =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      'An unexpected error occurred';
    return Promise.reject(new Error(message));
  }
);

export default apiClient;
